// src/useLaneLifecycle.ts
import * as React from "react";

// src/laneRerunSchedule.ts
import { LANE_STUCK_AFTER_MS, laneNeedsRerun } from "@hopdrive/sdk-lanes";
var LANE_STALLED_RETRY_MS = 6e4;
var LANE_STALLED_RETRY_LIMIT = 3;
function initialLaneRerunState() {
  return { observed: null, seenAt: null, rerunAt: null, stalledRetries: 0 };
}
function observedPair(lane) {
  return { status: lane.status ?? null, updatedat: lane.updatedat ?? null };
}
function sameLaneRead(a, b) {
  return !!a && (a.status ?? null) === (b.status ?? null) && (a.updatedat ?? null) === (b.updatedat ?? null);
}
function nextLaneRerunState(state, event) {
  switch (event.type) {
    case "observed": {
      const pair = observedPair(event.lane);
      return sameLaneRead(state.observed, pair) ? state : { ...state, observed: pair, seenAt: event.now };
    }
    case "rerun-attempted":
      return {
        ...state,
        ...event.landed && { observed: observedPair(event.landed) },
        rerunAt: event.now,
        stalledRetries: state.stalledRetries + (event.stalled ? 1 : 0)
      };
    case "reset":
      return initialLaneRerunState();
  }
}
var NOTHING = { stalled: false, gaveUp: false, next: { do: "nothing" } };
function laneRerunSchedule({
  lane,
  state,
  now
}) {
  const candidate = { status: laneLifecycleStatus(lane), updatedat: lane.updatedat };
  if (laneNeedsRerun(candidate, Number.POSITIVE_INFINITY) === null) return NOTHING;
  if (state.seenAt === null) return NOTHING;
  if (state.rerunAt === null) {
    const reason2 = laneNeedsRerun(candidate, now - state.seenAt);
    return reason2 ? { ...NOTHING, next: { do: "rerun", reason: reason2 } } : wait(false, state.seenAt + LANE_STUCK_AFTER_MS);
  }
  const changedSinceRerun = state.seenAt > state.rerunAt;
  const quietSince = changedSinceRerun ? state.seenAt : state.rerunAt;
  const stalled = !changedSinceRerun && state.stalledRetries > 0 || now - quietSince >= LANE_STUCK_AFTER_MS;
  if (!stalled) return wait(false, quietSince + LANE_STUCK_AFTER_MS);
  const dueAt = state.rerunAt + LANE_STALLED_RETRY_MS;
  if (now < dueAt) return wait(true, dueAt);
  if (state.stalledRetries >= LANE_STALLED_RETRY_LIMIT) return { stalled: true, gaveUp: true, next: { do: "nothing" } };
  const reason = laneNeedsRerun(candidate, now - state.seenAt);
  return { stalled: true, gaveUp: false, next: reason ? { do: "rerun", reason } : { do: "nothing" } };
}
function wait(stalled, at) {
  return { stalled, gaveUp: false, next: { do: "wait", at } };
}

// src/useLaneLifecycle.ts
var STATUSES = /* @__PURE__ */ new Set(["new", "pending", "enriching", "ready", "failed", "stale"]);
function laneLifecycleStatus(lane) {
  return typeof lane?.status === "string" && STATUSES.has(lane.status) ? lane.status : null;
}
function laneLifecyclePhase(lane) {
  if (!lane?.id) return "idle";
  const status = laneLifecycleStatus(lane);
  if (status === "failed" || status === "stale") return "failed";
  if (status === "ready") return "ready";
  if (status === "new" || status === "pending" || status === "enriching") return "enriching";
  return "failed";
}
function lanesReadyForRequest(mainLane, inverseLane, requiresInverseLane = false) {
  if (!mainLane?.id || laneLifecyclePhase(mainLane) !== "ready") return false;
  return !requiresInverseLane || !!inverseLane?.id && laneLifecyclePhase(inverseLane) === "ready";
}
var NO_RERUN = { stalled: false, gaveUp: false, next: { do: "nothing" } };
function useLaneLifecycle({
  lane,
  generation,
  useSubscription,
  retryLane,
  rerunLane,
  onUpdate
}) {
  const laneId = lane?.id ?? null;
  const identity = `${generation}:${laneId ?? "none"}`;
  const identityRef = React.useRef(identity);
  identityRef.current = identity;
  const [observed, setObserved] = React.useState(null);
  const [retryingIdentity, setRetryingIdentity] = React.useState(null);
  const [retryError, setRetryError] = React.useState(null);
  const effectiveLane = observed?.identity === identity ? observed.lane : lane;
  const phase = laneLifecyclePhase(effectiveLane);
  const subscription = useSubscription({ laneId, skip: phase !== "enriching", generation });
  const adopt = React.useCallback(
    (next) => {
      setObserved({ identity, lane: next });
      onUpdate?.(next);
    },
    [identity, onUpdate]
  );
  React.useEffect(() => {
    const next = subscription.lane;
    if (subscription.generation !== generation || !next?.id || next.id !== laneId || identityRef.current !== identity) {
      return;
    }
    adopt(next);
  }, [adopt, generation, identity, laneId, subscription.generation, subscription.lane]);
  const retry = React.useCallback(async () => {
    if (!laneId || !retryLane || identityRef.current !== identity) return;
    setRetryError(null);
    setRetryingIdentity(identity);
    try {
      const next = await retryLane(laneId);
      if (!next?.id || next.id !== laneId || identityRef.current !== identity) return;
      adopt(next);
    } catch (error) {
      if (identityRef.current === identity) setRetryError({ identity, error });
    } finally {
      if (identityRef.current === identity) setRetryingIdentity(null);
    }
  }, [adopt, identity, laneId, retryLane]);
  const rerun = useLaneRerun({ identity, identityRef, lane: effectiveLane, laneId, rerunLane, adopt });
  const currentError = retryError?.identity === identity ? retryError.error : rerun.error !== void 0 ? rerun.error : subscription.generation === generation ? subscription.error : void 0;
  return {
    laneId,
    status: laneLifecycleStatus(effectiveLane),
    phase,
    enriching: phase === "enriching",
    ready: phase === "ready",
    failed: phase === "failed",
    retrying: retryingIdentity === identity,
    stalled: rerun.schedule.stalled,
    gaveUp: rerun.schedule.gaveUp,
    error: currentError,
    retry
  };
}
function useLaneRerun({
  identity,
  identityRef,
  lane,
  laneId,
  rerunLane,
  adopt
}) {
  const [tracked, setTracked] = React.useState(() => ({
    identity,
    state: initialLaneRerunState()
  }));
  const [rerunError, setRerunError] = React.useState(null);
  const [clock, setClock] = React.useState(0);
  const inFlightRef = React.useRef(null);
  const apply = React.useCallback((forIdentity, event) => {
    setTracked((previous) => {
      const base = previous.identity === forIdentity ? previous.state : nextLaneRerunState(previous.state, { type: "reset" });
      const state2 = nextLaneRerunState(base, event);
      return previous.identity === forIdentity && state2 === previous.state ? previous : { identity: forIdentity, state: state2 };
    });
  }, []);
  const following = !!rerunLane && !!lane?.id && lane.id === laneId;
  const status = lane?.status ?? null;
  const updatedat = lane?.updatedat ?? null;
  React.useEffect(() => {
    if (following && lane) apply(identity, { type: "observed", lane, now: Date.now() });
  }, [apply, following, identity, status, updatedat]);
  const state = tracked.identity === identity ? tracked.state : null;
  const schedule = following && lane && state ? laneRerunSchedule({ lane, state, now: Date.now() }) : NO_RERUN;
  const latest = React.useRef({ lane, schedule, rerunLane });
  latest.current = { lane, schedule, rerunLane };
  const next = schedule.next;
  const nextKey = next.do === "wait" ? `wait:${next.at}:${clock}` : next.do === "rerun" ? `rerun:${next.reason}` : "nothing";
  React.useEffect(() => {
    if (next.do === "wait") {
      const timer = setTimeout(() => setClock((tick) => tick + 1), Math.max(0, next.at - Date.now()));
      return () => clearTimeout(timer);
    }
    if (next.do !== "rerun" || inFlightRef.current === identity) return;
    const { lane: target, schedule: decided, rerunLane: run } = latest.current;
    if (!target || !run) return;
    inFlightRef.current = identity;
    setRerunError(null);
    void (async () => {
      let landed = null;
      try {
        const result = await run(target, next.reason);
        if (result && result.id === target.id && sameLaneRead(latest.current.lane, target)) landed = result;
      } catch (error) {
        if (identityRef.current === identity) setRerunError({ identity, error });
      } finally {
        if (inFlightRef.current === identity) inFlightRef.current = null;
      }
      if (identityRef.current !== identity) return;
      apply(identity, { type: "rerun-attempted", now: Date.now(), stalled: decided.stalled, landed });
      if (landed) adopt(landed);
    })();
  }, [identity, nextKey]);
  return { schedule, error: rerunError?.identity === identity ? rerunError.error : void 0 };
}
export {
  LANE_STALLED_RETRY_LIMIT,
  LANE_STALLED_RETRY_MS,
  initialLaneRerunState,
  laneLifecyclePhase,
  laneLifecycleStatus,
  laneRerunSchedule,
  lanesReadyForRequest,
  nextLaneRerunState,
  sameLaneRead,
  useLaneLifecycle
};
//# sourceMappingURL=index.mjs.map